/* @flow */

import adconfig from '../adconfig';
import { logError } from '../utils/logger';
import { extend, isObject } from '../utils/coreutils';
import {
  loadMultipleJSWithTimeout,
  getEvaluatedDialup,
  getuolenv
} from '../utils/browserutil';

/**
 * @module _adserverbase
 * @exports _adserverbase
 * @description Provides interface for all ad servers and common functionality
 */

/**
 * @global
 * @typedef _perAdServerConfig
 * @property {string} lib Library path
 * @property {number} timeout timeout value
 */

class _adserverbase {
  _doc: Document;

  _win: WindowProxy;

  _name: string;

  _isDialup: boolean;

  _brand: string;

  _QPVars: QPParams;

  // Flow 0.176: CovariantOf removed; subclasses declare partner-specific config shapes.
  // flowlint-next-line unclear-type:off
  _defConfig: any;

  // flowlint-next-line unclear-type:off
  _config: any;

  /**
   * @class
   */
  constructor() {
    this._doc = document;
    this._win = window;
    this._isDialup = false;
    this._brand = '';
    this._QPVars = {};
    this._defConfig = {
      enableSendAllBids: 0,
      lib: '',
      timeout: 10000
    };
    this._config = extend(true, this._defConfig);
  }

  /**
   * Initialize the object - called from adserver initialize
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: WindowProxy, d: Document, g: globalParam) {
    this._doc = d;
    this._win = w;
    this._isDialup = g.isDialup;
    this._brand = g.phInfo.brand;
    this._QPVars = g.QPVars;
  }

  _preUpdateConfig() {
    // Dummy funcion to be overidden in child class if needed
  }

  /**
   * Update config - called from adserver updateConfig
   *
   * @param {_perAdServerConfig} o - adserver config from adconfig
   * @returns {undefined}
   */
  updateConfig(o: ?_perAdServerConfig) {
    // avoid doing any config work if given ad server is not configured in JSAPI Config
    if (!isObject(o)) {
      return;
    }
    this._preUpdateConfig();
    this._config = extend(true, {}, this._defConfig, o);
    this._postUpdateConfig();
  }

  _postUpdateConfig() {
    // Dummy funcion to be overidden in child class if needed
  }

  /**
   * Whether any servable position uses this ad server (excludes excludePositions).
   *
   * @returns {boolean}
   * @protected
   */
  _hasPositions(): boolean {
    return adconfig.hasPositionsForAdServer(this._name);
  }

  /**
   * getName - getter for name
   *
   * @returns {string} name
   */
  getName(): string {
    return this._name;
  }

  _getCustomVars(
    pos: string,
    o: hbPartnerData,
    customVars: CustomVars,
    bRotation: boolean
  ): CustomVars {
    const params = o && o[pos] ? o[pos] : {};
    return extend(
      true,
      {
        UOL_POS: pos,
        UOL_PAGENAME: adconfig.getPageName(),
        UOL_ADROTATE: bRotation
      },
      customVars,
      params._CV,
      {
        _QP: this._getQueryParams(pos, o, bRotation)
      }
    );
  }

  /**
   * Get ad server parameter
   *
   * @private
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   */
  _getOpenxParams(bRotation: boolean): anyObject {
    return extend(true, {}, this._QPVars, {
      uolpagename: adconfig.getPageName(),
      uoldialup: getEvaluatedDialup(this._isDialup),
      uolbrand: this._brand,
      uolrotate: bRotation
    });
  }

  _postInit() {
    /* dummy func for post init after library is loaded */
  }

  _getBidsForParams(pos: string = '', o: hbPartnerData = {}): qparams {
    let ret: qparams = {};
    if (!pos.length || !isObject(o) || !isObject(o[pos])) {
      return ret;
    }
    const bidInfo: hbPosData = o[pos] || {};
    const { winningBid = { hb_pb: '', hb_bidder: '', hb_adid: '' }, allBids } =
      bidInfo;
    // add winning bid only the params if it's valid one
    if (
      winningBid.hb_pb.length > 0 &&
      winningBid.hb_adid.length > 0 &&
      winningBid.hb_bidder.length > 0
    ) {
      ret = extend(true, ret, winningBid);
    }
    if (this._config.enableSendAllBids === 2) {
      ret = extend(true, ret, ...allBids);
    } else if (this._config.enableSendAllBids === 1) {
      ret = extend(true, ...allBids);
    }
    return ret;
  }

  _getQueryParamsCommon(bRotation: boolean): qparams {
    const qparams = extend(
      true,
      {},
      this._getOpenxParams(bRotation),
      getuolenv()
    );
    const qpStringyValues: { [string]: string } = {};
    Object.keys(qparams).forEach(key => {
      qpStringyValues[key] = String(qparams[key]);
    });
    return qpStringyValues;
  }

  _getQueryParamsPosition(pos: string, o: hbPartnerData): qparams {
    const qparams = extend(
      true,
      {
        UOL_POS: pos
      },
      this._getBidsForParams(pos, o)
    );
    const qpStringyValues: { [string]: string } = {};
    Object.keys(qparams).forEach(key => {
      qpStringyValues[key] = String(qparams[key]);
    });
    return qpStringyValues;
  }

  _getQueryParams(pos: string, o: hbPartnerData, bRotation: boolean): qparams {
    const qparams = extend(
      true,
      {},
      this._getQueryParamsPosition(pos, o),
      this._getQueryParamsCommon(bRotation)
    );
    const qpStringyValues: { [string]: string } = {};
    Object.keys(qparams).forEach(key => {
      qpStringyValues[key] = String(qparams[key]);
    });
    return qpStringyValues;
  }

  _loadLibrary(
    libPath: string | Array<string> = '',
    bAsync: boolean = false,
    libLoadMode?: 'parallel' | 'sequential' = 'parallel'
  ) {
    // Normalize to array: convert single string to array, or use array as-is
    let scripts: Array<string> = [];
    if (Array.isArray(libPath)) {
      scripts = libPath;
    } else if (libPath) {
      scripts = [libPath];
    }

    if (scripts.length === 0) {
      return;
    }

    // Always use loadMultipleJSWithTimeout (handles both single and multiple scripts)
    // Internally uses loadJS which loads in <head> at the end with async when bAsync is true
    loadMultipleJSWithTimeout(
      scripts,
      this._doc,
      bAsync,
      libLoadMode || 'parallel',
      0 // No timeout for ad server libraries
    ).catch(error => {
      logError(`${this._name} failed to load library scripts: ${error}`);
    });
  }

  _isSupported(): boolean {
    return true;
  }

  refreshPrivacySignals(): void {
    /* override in child ad servers that react to consent changes */
  }

  clearAdRequests(
    _pageName: string,
    _posList: Array<string> = [],
    _bRotation: boolean = false
  ): void {
    // eslint-disable-next-line no-unused-vars
    const _ = _pageName;
    // eslint-disable-next-line no-unused-vars
    const __ = _posList;
    // eslint-disable-next-line no-unused-vars
    const ___ = _bRotation;
    /* dummy func for post init after library is loaded */
  }

  /**
   * Loads openx static using adloader
   *
   * @param {Array<string>} posList - position list array
   * @param {object.<string, module:_bidbase.hbPosData>} o - header bidding return object
   * @param {object} customVars - custom variable to be populated inside iframe
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   */
  /* eslint-disable no-unused-vars */
  loadAds(
    posList: Array<string>,
    o: hbPartnerData,
    customVars: CustomVars,
    bRotation: boolean
  ) {
    /* eslint-enable no-unused-vars */
    // Ideally, Base class loadAds() should not be called. Each child class should override it.
    logError(`${this._name} Do not class base class loadAds()`);
  }
}

export default _adserverbase;
